import React, {useEffect, useState} from "react";
import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Select,
    SimpleGrid,
    Text,
    Textarea,
    useToast,
    VStack,
} from "@chakra-ui/react";
import {categories} from "../data/catalog";
import {site} from "../data/site";

const EMPTY = {firstName: "", lastName: "", email: "", phone: "", product: "", message: "", botcheck: false};

const WEB3FORMS_URL = "https://api.web3forms.com/submit";

function validate(values) {
    const errors = {};
    if (!values.firstName.trim()) errors.firstName = "Please enter your first name";
    if (!values.lastName.trim()) errors.lastName = "Please enter your last name";
    if (!values.email.trim()) errors.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = "Please enter a valid email";
    if (!values.phone.trim()) errors.phone = "Please enter your phone number";
    if (!values.message.trim()) errors.message = "Please tell us a little about your project";
    return errors;
}

// Sends the request through Web3Forms (no backend needed). Returns true when the email was accepted.
async function sendQuote(values) {
    const response = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: {"Content-Type": "application/json", Accept: "application/json"},
        body: JSON.stringify({
            access_key: site.quoteFormKey,
            subject: `New quote request - ${values.firstName.trim()} ${values.lastName.trim()}`,
            // Web3Forms uses `email` as the reply-to address, so replying goes straight to the customer
            name: `${values.firstName.trim()} ${values.lastName.trim()}`,
            email: values.email.trim(),
            phone: values.phone.trim(),
            product: values.product || "Not specified",
            message: values.message.trim(),
            botcheck: values.botcheck,
        }),
    });
    const result = await response.json().catch(() => ({}));
    return response.ok && result.success === true;
}

function QuoteForm({defaultProduct = "", onSent}) {
    const toast = useToast();
    const [values, setValues] = useState({...EMPTY, product: defaultProduct});
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);

    useEffect(() => {
        setValues((v) => ({...v, product: defaultProduct}));
    }, [defaultProduct]);

    const update = (field) => (e) => {
        setValues((v) => ({...v, [field]: e.target.value}));
        if (errors[field]) setErrors((errs) => ({...errs, [field]: undefined}));
    };

    async function handleSubmit(e) {
        e.preventDefault();
        const found = validate(values);
        setErrors(found);
        if (Object.keys(found).length) return;

        if (!site.quoteFormKey) {
            console.error("Quote form: set site.quoteFormKey in data/site.js (Web3Forms access key).");
            toast({status: "error", title: "Unable to send right now", description: `Please call us on ${site.phone}.`});
            return;
        }

        setSending(true);
        try {
            if (await sendQuote(values)) {
                toast({status: "success", title: "Thanks, your request has been sent", description: "We will be in touch shortly."});
                setValues({...EMPTY, product: defaultProduct});
                onSent && onSent();
            } else {
                toast({status: "error", title: "Something went wrong", description: `Please try again or call us on ${site.phone}.`});
            }
        } catch (err) {
            toast({status: "error", title: "Something went wrong", description: `Please try again or call us on ${site.phone}.`});
        } finally {
            setSending(false);
        }
    }

    return (
        <VStack as="form" onSubmit={handleSubmit} noValidate spacing={5} align="stretch">
            <SimpleGrid columns={{base: 1, md: 2}} spacing={5}>
                <FormControl isRequired isInvalid={!!errors.firstName}>
                    <FormLabel>First name</FormLabel>
                    <Input value={values.firstName} onChange={update("firstName")} autoComplete="given-name"/>
                    <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.lastName}>
                    <FormLabel>Last name</FormLabel>
                    <Input value={values.lastName} onChange={update("lastName")} autoComplete="family-name"/>
                    <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel>Email</FormLabel>
                    <Input type="email" value={values.email} onChange={update("email")} autoComplete="email"/>
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.phone}>
                    <FormLabel>Phone number</FormLabel>
                    <Input type="tel" value={values.phone} onChange={update("phone")} autoComplete="tel" placeholder="04xx xxx xxx"/>
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>
            </SimpleGrid>
            <FormControl>
                <FormLabel>Product</FormLabel>
                <Select value={values.product} onChange={update("product")} placeholder="Select a product (optional)">
                    {categories.map((category) => (
                        <option key={category.path} value={category.title}>{category.title}</option>
                    ))}
                    <option value="Other carpentry / joinery">Other carpentry / joinery</option>
                </Select>
            </FormControl>
            <FormControl isRequired isInvalid={!!errors.message}>
                <FormLabel>Project details</FormLabel>
                <Textarea value={values.message} onChange={update("message")} rows={5}
                          placeholder="Tell us about your project: size, location, timing..."/>
                <FormErrorMessage>{errors.message}</FormErrorMessage>
            </FormControl>
            {/* Spam trap: hidden from people; bots that tick it are dropped by Web3Forms */}
            <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" style={{display: "none"}}
                   checked={values.botcheck} onChange={(e) => setValues((v) => ({...v, botcheck: e.target.checked}))}/>
            <Button type="submit" size="lg" isLoading={sending} loadingText="Sending">
                Send request
            </Button>
            <Text fontSize="sm" color="ink.500" textAlign="center">
                Prefer to talk? Call us on {site.phone}.
            </Text>
        </VStack>
    );
}

export default QuoteForm;
